import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TeamGuide, useOnboarding } from "@/features/onboarding";
import { RoleService } from "@/gen/omnios/v1/rbac_connect.js";
import { transport } from "@/lib/grpc";
import {
	type Member,
	type MemberRole,
	type PendingInvite,
	useMembersStore,
} from "@/lib/members";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useSlashFocus } from "@/lib/use-slash-focus";
import { createClient } from "@connectrpc/connect";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
	Copy,
	Link2,
	MoreHorizontal,
	Plus,
	Search,
	Send,
	Trash2,
	X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/members")({
	component: MembersSettingsPage,
});

interface RoleOption {
	name: string;
	displayName: string;
	isSystem: boolean;
}

const roleClient = createClient(RoleService, transport);

function roleBadgeVariant(
	role: string,
	roleMap: Map<string, RoleOption>,
): "default" | "secondary" | "outline" {
	const def = roleMap.get(role);
	if (def?.isSystem) return "secondary";
	if (def) return "outline";
	return "outline";
}

function roleLabel(role: string, roleMap: Map<string, RoleOption>): string {
	return roleMap.get(role)?.displayName ?? role;
}

function MembersSettingsPage() {
	const { t } = useTranslation("settings");

	useBreadcrumbs([
		{
			label: t("breadcrumb_settings"),
			href: "/settings",
		},
		{ label: t("members_breadcrumb") },
	]);

	const {
		members,
		pendingInvites,
		inviteLinks,
		isLoading,
		fetchMembers,
		updateMemberRole,
		removeMember,
		inviteByEmail,
		createInviteLink,
		revokeInviteLink,
		cancelInvite,
		fetchInvites,
		fetchInviteLinks,
	} = useMembersStore();

	const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
	const roleMap = new Map(roleOptions.map((r) => [r.name, r]));
	const assignableRoles = roleOptions.filter((r) => r.name !== "owner");

	useEffect(() => {
		fetchMembers();
		fetchInvites();
		fetchInviteLinks();
		roleClient.listRoles({}).then((resp) => {
			setRoleOptions(
				resp.roles.map((r) => ({
					name: r.name,
					displayName: r.displayName || r.name,
					isSystem: r.isSystem,
				})),
			);
		});
	}, [fetchMembers, fetchInvites, fetchInviteLinks]);

	const [search, setSearch] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);
	const clearSearch = useCallback(() => setSearch(""), []);
	useSlashFocus(searchRef, clearSearch);
	const [inviteOpen, setInviteOpen] = useState(false);
	const [linkDialogOpen, setLinkDialogOpen] = useState(false);
	const [removeTarget, setRemoveTarget] = useState<Member | null>(null);

	useEffect(() => {
		fetchMembers(search.trim());
	}, [search, fetchMembers]);

	const filteredMembers = members;

	const memberColumns: ColumnDef<Member, unknown>[] = [
		{
			accessorKey: "name",
			header: t("members_col_member"),
			cell: ({ row }) => {
				const m = row.original;
				const displayName = m.name || m.email || m.id.slice(0, 8);
				const ini =
					displayName
						.split(/\s+/)
						.filter(Boolean)
						.map((w) => w[0])
						.join("")
						.slice(0, 2)
						.toUpperCase() || "?";
				return (
					<div className="flex items-center gap-3">
						{m.avatarUrl ? (
							<img
								src={m.avatarUrl}
								alt={displayName}
								className="h-8 w-8 shrink-0 rounded-full object-cover"
							/>
						) : (
							<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
								{ini}
							</div>
						)}
						<div>
							<p className="text-sm font-medium leading-tight">
								{m.name || (
									<span className="text-muted-foreground italic">
										{t("members_no_name")}
									</span>
								)}
							</p>
							<p className="text-xs text-muted-foreground leading-tight">
								{m.email || "—"}
							</p>
						</div>
					</div>
				);
			},
		},
		{
			accessorKey: "role",
			header: t("members_col_role"),
			cell: ({ row }) => (
				<Badge variant={roleBadgeVariant(row.original.role, roleMap)}>
					{roleLabel(row.original.role, roleMap)}
				</Badge>
			),
		},
		{
			accessorKey: "joinedAt",
			header: t("members_col_joined"),
			cell: ({ row }) =>
				new Date(row.original.joinedAt).toLocaleDateString("ru-RU"),
		},
		{
			id: "actions",
			header: "",
			cell: ({ row }) => {
				const m = row.original;
				if (m.role === "owner") return null;
				return (
					<MemberActions
						member={m}
						roles={assignableRoles}
						roleMap={roleMap}
						onRoleChange={(role) => updateMemberRole(m.id, role)}
						onRemove={() => setRemoveTarget(m)}
					/>
				);
			},
		},
	];

	const pendingColumns: ColumnDef<PendingInvite, unknown>[] = [
		{
			accessorKey: "email",
			header: "Email",
			cell: ({ row }) => <span className="text-sm">{row.original.email}</span>,
		},
		{
			accessorKey: "role",
			header: t("members_col_role"),
			cell: ({ row }) => (
				<Badge variant={roleBadgeVariant(row.original.role, roleMap)}>
					{roleLabel(row.original.role, roleMap)}
				</Badge>
			),
		},
		{
			accessorKey: "sentAt",
			header: t("members_col_sent"),
			cell: ({ row }) =>
				new Date(row.original.sentAt).toLocaleDateString("ru-RU"),
		},
		{
			id: "actions",
			header: "",
			cell: ({ row }) => (
				<Button
					variant="ghost"
					size="icon"
					onClick={() => cancelInvite(row.original.id)}
				>
					<X className="h-4 w-4" />
				</Button>
			),
		},
	];

	return (
		<div className="space-y-6">
			<TeamGuide />
			<div className="flex items-center justify-between gap-4">
				<div className="relative">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						ref={searchRef}
						placeholder={t("members_search_placeholder")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9 h-9 w-56"
					/>
				</div>
				<div className="flex items-center gap-2">
					<Button
						variant="outline"
						size="sm"
						className="h-9"
						onClick={() => {
							fetchMembers();
							fetchInvites();
							fetchInviteLinks();
						}}
					>
						{t("refresh", { defaultValue: "Обновить" })}
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => setLinkDialogOpen(true)}
					>
						<Link2 className="h-4 w-4" />
						{t("members_invite_link")}
					</Button>
					<Button
						size="sm"
						onClick={() => setInviteOpen(true)}
						data-onboarding="invite-member"
					>
						<Plus className="h-4 w-4" />
						{t("members_invite")}
					</Button>
				</div>
			</div>

			<Tabs defaultValue="members">
				<TabsList>
					<TabsTrigger value="members">
						{t("members_tab_members", { count: filteredMembers.length })}
					</TabsTrigger>
					<TabsTrigger value="pending">
						{t("members_tab_pending", { count: pendingInvites.length })}
					</TabsTrigger>
					<TabsTrigger value="links">
						{t("members_tab_links", { count: inviteLinks.length })}
					</TabsTrigger>
				</TabsList>

				<TabsContent value="members">
					{isLoading ? (
						<div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
							{t("common:loading")}
						</div>
					) : (
						<DataTable columns={memberColumns} data={filteredMembers} />
					)}
				</TabsContent>

				<TabsContent value="pending">
					{pendingInvites.length === 0 ? (
						<div className="flex h-32 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
							{t("members_no_pending")}
						</div>
					) : (
						<DataTable columns={pendingColumns} data={pendingInvites} />
					)}
				</TabsContent>

				<TabsContent value="links">
					<InviteLinksTab
						links={inviteLinks}
						roleMap={roleMap}
						onRevoke={revokeInviteLink}
					/>
				</TabsContent>
			</Tabs>

			<InviteDialog
				open={inviteOpen}
				onOpenChange={setInviteOpen}
				onInvite={inviteByEmail}
				roles={assignableRoles}
			/>

			<InviteLinkDialog
				open={linkDialogOpen}
				onOpenChange={setLinkDialogOpen}
				onCreate={createInviteLink}
				roles={assignableRoles}
			/>

			<AlertDialog
				open={!!removeTarget}
				onOpenChange={(open) => {
					if (!open) setRemoveTarget(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("members_remove_title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("members_remove_desc", {
								name: removeTarget?.name,
								email: removeTarget?.email,
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (removeTarget) {
									removeMember(removeTarget.id);
									setRemoveTarget(null);
								}
							}}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

function MemberActions({
	member,
	roles,
	roleMap,
	onRoleChange,
	onRemove,
}: {
	member: Member;
	roles: RoleOption[];
	roleMap: Map<string, RoleOption>;
	onRoleChange: (role: MemberRole) => void;
	onRemove: () => void;
}) {
	const { t } = useTranslation("settings");

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon">
					<MoreHorizontal className="h-4 w-4" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				{roles.map((r) => (
					<DropdownMenuItem
						key={r.name}
						disabled={r.name === member.role}
						onSelect={() => onRoleChange(r.name as MemberRole)}
					>
						{t("members_assign", { role: roleLabel(r.name, roleMap) })}
					</DropdownMenuItem>
				))}
				<DropdownMenuSeparator />
				<DropdownMenuItem
					className="text-destructive focus:text-destructive"
					onSelect={onRemove}
				>
					<Trash2 className="h-4 w-4" />
					{t("common:delete")}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function InviteDialog({
	open,
	onOpenChange,
	onInvite,
	roles,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	onInvite: (email: string, role: MemberRole) => Promise<void>;
	roles: RoleOption[];
}) {
	const { t } = useTranslation("settings");
	const [email, setEmail] = useState("");
	const { completeStep } = useOnboarding();
	const defaultRole =
		roles.find((r) => r.name === "member")?.name ?? roles[0]?.name ?? "member";
	const [role, setRole] = useState(defaultRole);
	const [sending, setSending] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async () => {
		if (!email.trim()) return;
		setSending(true);
		setError(null);
		try {
			await onInvite(email.trim(), role as MemberRole);
			setEmail("");
			setRole(defaultRole);
			completeStep("teamInvited", 5);
			onOpenChange(false);
		} catch (e) {
			setError(e instanceof Error ? e.message : t("members_invite_error"));
		} finally {
			setSending(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("members_invite_title")}</DialogTitle>
					<DialogDescription>{t("members_invite_desc")}</DialogDescription>
				</DialogHeader>
				<div className="space-y-4 py-2">
					<div className="space-y-1.5">
						<Label>Email</Label>
						<Input
							type="email"
							placeholder="user@example.com"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleSubmit();
							}}
						/>
					</div>
					<div className="space-y-1.5">
						<Label>
							{t("members_invite_role")}
						</Label>
						<Select value={role} onValueChange={setRole}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{roles.map((r) => (
									<SelectItem key={r.name} value={r.name}>
										{r.displayName}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
				{error && <p className="text-sm text-destructive">{error}</p>}
				<DialogFooter>
					<Button onClick={handleSubmit} disabled={!email.trim() || sending}>
						<Send className="h-4 w-4" />
						{sending ? t("members_sending") : t("members_send")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function InviteLinkDialog({
	open,
	onOpenChange,
	onCreate,
	roles,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	onCreate: (role: MemberRole) => Promise<{ token: string }>;
	roles: RoleOption[];
}) {
	const { t } = useTranslation("settings");
	const defaultRole =
		roles.find((r) => r.name === "member")?.name ?? roles[0]?.name ?? "member";
	const [role, setRole] = useState(defaultRole);
	const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
	const [creating, setCreating] = useState(false);
	const [copied, setCopied] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleCreate = async () => {
		setCreating(true);
		setError(null);
		try {
			const link = await onCreate(role as MemberRole);
			setGeneratedUrl(`${window.location.origin}/invite/${link.token}`);
		} catch (e) {
			setError(e instanceof Error ? e.message : t("members_link_error"));
		} finally {
			setCreating(false);
		}
	};

	const handleCopy = useCallback(() => {
		if (!generatedUrl) return;
		navigator.clipboard.writeText(generatedUrl);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}, [generatedUrl]);

	const handleClose = (v: boolean) => {
		if (!v) {
			setGeneratedUrl(null);
			setCopied(false);
			setRole(defaultRole);
			setError(null);
		}
		onOpenChange(v);
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("members_link_title")}</DialogTitle>
					<DialogDescription>{t("members_link_desc")}</DialogDescription>
				</DialogHeader>
				<div className="space-y-4 py-2">
					<div className="space-y-1.5">
						<Label>
							{t("members_link_role_label")}
						</Label>
						<Select
							value={role}
							onValueChange={setRole}
							disabled={!!generatedUrl}
						>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{roles.map((r) => (
									<SelectItem key={r.name} value={r.name}>
										{r.displayName}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{generatedUrl && (
						<div className="space-y-1.5">
							<Label>
								{t("members_link_label")}
							</Label>
							<div className="flex gap-2">
								<Input
									readOnly
									value={generatedUrl}
									className="font-mono text-xs"
								/>
								<Button variant="outline" size="icon" onClick={handleCopy}>
									<Copy className="h-4 w-4" />
								</Button>
							</div>
							{copied && (
								<p className="text-xs text-muted-foreground">
									{t("members_link_copied")}
								</p>
							)}
						</div>
					)}
				</div>
				{error && <p className="text-sm text-destructive">{error}</p>}
				<DialogFooter>
					{generatedUrl ? (
						<Button variant="outline" onClick={() => handleClose(false)}>
							{t("members_link_done")}
						</Button>
					) : (
						<Button onClick={handleCreate} disabled={creating}>
							<Link2 className="h-4 w-4" />
							{creating ? t("members_link_creating") : t("members_link_create")}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function InviteLinksTab({
	links,
	roleMap,
	onRevoke,
}: {
	links: {
		id: string;
		token: string;
		role: MemberRole;
		createdAt: string;
		expiresAt: string;
	}[];
	roleMap: Map<string, RoleOption>;
	onRevoke: (id: string) => void;
}) {
	const { t } = useTranslation("settings");
	const [copiedId, setCopiedId] = useState<string | null>(null);

	const handleCopy = (id: string, token: string) => {
		const url = `${window.location.origin}/invite/${token}`;
		navigator.clipboard.writeText(url);
		setCopiedId(id);
		setTimeout(() => setCopiedId(null), 2000);
	};

	if (links.length === 0) {
		return (
			<div className="flex h-32 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
				{t("members_no_links")}
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{links.map((link) => (
				<div
					key={link.id}
					className="flex items-center gap-3 rounded-md border px-4 py-3"
				>
					<div className="flex-1 min-w-0">
						<p className="truncate font-mono text-xs text-muted-foreground">
							{`${window.location.origin}/invite/${link.token}`}
						</p>
						<div className="mt-1 flex items-center gap-2">
							<Badge variant={roleBadgeVariant(link.role, roleMap)}>
								{roleLabel(link.role, roleMap)}
							</Badge>
							<span className="text-xs text-muted-foreground">
								{t("members_link_expires", {
									date: new Date(link.expiresAt).toLocaleDateString("ru-RU"),
								})}
							</span>
						</div>
					</div>
					<Button
						variant="ghost"
						size="icon"
						onClick={() => handleCopy(link.id, link.token)}
					>
						{copiedId === link.id ? (
							<span className="text-xs">✓</span>
						) : (
							<Copy className="h-4 w-4" />
						)}
					</Button>
					<Button
						variant="ghost"
						size="icon"
						className="text-muted-foreground hover:text-destructive"
						onClick={() => onRevoke(link.id)}
					>
						<Trash2 className="h-4 w-4" />
					</Button>
				</div>
			))}
		</div>
	);
}
